import { ArrowLeftIcon, UserIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import ConfirmModal from '@/components/ConfirmModal'

import { BASE } from './_Layout'
import InfoTip from './InfoTip'
import { caseloadFor, MockMemberDetail, MOCK_FORMER_MEMBER_DETAILS, MOCK_MEMBER_DETAILS } from './mockData'
import { useProviderSession } from './previewSession'
import { formatSignIn, TEAM_ROLE_LABELS } from './ProviderTeam'

// Humanize audit-trail action keys ("provider.homework.assign") for display.
const ACTION_LABELS: Record<string, string> = {
    'provider.license.list': 'Viewed the license roster',
    'provider.license.invite': 'Invited a client',
    'provider.license.invite-denied': 'Attempted to invite a client (denied)',
    'provider.license.resend': 'Resent a client invitation',
    'provider.license.revoke': 'Revoked a license',
    'provider.family.list': 'Viewed the client roster',
    'provider.family.detail': 'Viewed a client',
    'provider.family.roadmap': 'Viewed a client roadmap',
    'provider.homework.list': 'Viewed homework for a client',
    'provider.homework.assign': 'Assigned homework',
    'provider.homework.assign-denied': 'Attempted to assign homework (denied)',
    'provider.homework.cancel': 'Cancelled homework',
    'provider.note.list': 'Viewed notes for a client',
    'provider.note.create': 'Added a note',
    'provider.note.update': 'Edited a note',
    'provider.note.delete': 'Deleted a note',
    'provider.analytics.read': 'Viewed analytics',
    'provider.member.read': 'Viewed a team member',
    'provider.member.add': 'Added a team member',
    'provider.member.role-change': "Changed a team member's role",
    'provider.member.remove': 'Removed a team member',
}

function actionLabel(action: string | null): string {
    if (!action) return 'Portal activity'
    if (ACTION_LABELS[action]) return ACTION_LABELS[action]
    return action.replace(/^provider\./, '').replace(/[.-]/g, ' ')
}

function formatTimestamp(value: string): string {
    const d = new Date(value)
    if (isNaN(d.valueOf())) return ''
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function ProviderTeamMemberDetail({ former = false }: { former?: boolean }) {
    const { id } = useParams()
    const navigate = useNavigate()
    const session = useProviderSession()

    const initial: MockMemberDetail | null =
        (former ? MOCK_FORMER_MEMBER_DETAILS[id ?? ''] : MOCK_MEMBER_DETAILS[id ?? '']) ?? null
    const [data, setData] = useState<MockMemberDetail | null>(initial)
    const [role, setRole] = useState(initial?.member.role ?? '')
    const [showRemove, setShowRemove] = useState(false)

    if (!data) {
        return (
            <div className='max-w-5xl mx-auto px-4 py-12 text-center text-sm text-neutral-500'>
                Could not load this team member.{' '}
                <Link to={`${BASE}/team`} className='text-brand-600 underline'>
                    Back to Team Members
                </Link>
            </div>
        )
    }

    const { member, engagement } = data
    const isSelf = member.email === session?.user.email

    function handleRoleSave() {
        setData({ ...data!, member: { ...member, role } })
    }

    async function handleRemove() {
        setShowRemove(false)
        navigate(`${BASE}/team`)
    }

    return (
        <div className='max-w-5xl mx-auto px-4 sm:px-6 py-8'>
            <Link to={`${BASE}/team`} className='inline-flex items-center gap-1.5 text-sm text-brand-600 hover:underline mb-4'>
                <ArrowLeftIcon className='w-4 h-4' />
                Team Members
            </Link>

            {/* Profile */}
            <div className='bg-white border border-neutral-200 rounded-2xl p-6 mb-6'>
                <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
                    <div className='flex items-center gap-4'>
                        <div className='w-14 h-14 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0'>
                            <UserIcon className='w-6 h-6 text-brand-700' />
                        </div>
                        <div>
                            <div className='flex items-center gap-2'>
                                <h1 className='text-xl font-bold text-neutral-900'>{member.name || member.email}</h1>
                                {former && (
                                    <span className='text-xs font-medium text-neutral-600 bg-neutral-100 border border-neutral-200 rounded-full px-2.5 py-1'>
                                        Former member
                                    </span>
                                )}
                            </div>
                            <p className='text-sm text-neutral-500'>{member.email}</p>
                            <p className='text-xs text-neutral-500 mt-0.5'>
                                {member.role ? TEAM_ROLE_LABELS[member.role] ?? member.role : 'Member'}
                                {member.addedAt &&
                                    ` · Added ${new Date(member.addedAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}`}
                                {former &&
                                    member.removedAt &&
                                    ` · Removed ${new Date(member.removedAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}${member.removedBy ? ` by ${member.removedBy}` : ''}`}
                            </p>
                        </div>
                    </div>

                    {!former && !isSelf && (
                        <div className='flex items-center gap-2'>
                            <select
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                className='px-3 py-2 border border-neutral-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500'
                            >
                                <option value='admin'>Admin</option>
                                <option value='coordinator'>Coordinator</option>
                                <option value='viewer'>Viewer</option>
                            </select>
                            <button
                                onClick={handleRoleSave}
                                disabled={role === member.role}
                                className='px-3 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg'
                            >
                                Save role
                            </button>
                            <button
                                onClick={() => setShowRemove(true)}
                                className='px-3 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg'
                            >
                                Remove
                            </button>
                        </div>
                    )}
                    {!former && isSelf && (
                        <span className='text-xs text-neutral-500'>
                            This is you. You cannot change your own role or remove yourself.
                        </span>
                    )}
                    {former && (
                        <span className='text-xs text-neutral-500'>Action history is preserved for auditing.</span>
                    )}
                </div>
            </div>

            {/* Engagement stats */}
            <div className={`grid gap-4 mb-6 ${member.role === 'coordinator' ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-3'}`}>
                <Stat label='Last sign-in' value={formatSignIn(member.lastSignInAt)} />
                <Stat label='Sign-ins (30 days)' value={String(engagement.signIns30d)} />
                <Stat
                    label='Actions (30 days)'
                    value={String(engagement.actions30d)}
                    info='Portal actions this member recorded in the last 30 days: roster and client views, notes, homework, invites, and similar.'
                />
                {member.role === 'coordinator' && (
                    <Stat
                        label='Caseload'
                        value={`${caseloadFor(member.userId)} clients`}
                        info='Clients currently assigned to this coordinator.'
                    />
                )}
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Action breakdown */}
                <div className='bg-white border border-neutral-200 rounded-2xl p-6'>
                    <h2 className='text-lg font-semibold text-neutral-900 mb-4'>What they do (30 days)</h2>
                    {engagement.actionsByType.length === 0 ? (
                        <p className='text-sm text-neutral-500'>No portal actions in the last 30 days.</p>
                    ) : (
                        <ul className='space-y-2'>
                            {engagement.actionsByType.map((a) => (
                                <li key={a.action} className='flex items-center justify-between text-sm'>
                                    <span className='text-neutral-700'>{actionLabel(a.action)}</span>
                                    <span className='font-medium text-neutral-900'>{a.count}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Recent actions */}
                <div className='bg-white border border-neutral-200 rounded-2xl p-6'>
                    <h2 className='text-lg font-semibold text-neutral-900 mb-4'>Recent actions</h2>
                    {engagement.recentActions.length === 0 ? (
                        <p className='text-sm text-neutral-500'>No recorded actions yet.</p>
                    ) : (
                        <ul className='space-y-3'>
                            {engagement.recentActions.map((a, i) => (
                                <li
                                    key={i}
                                    className='flex items-start gap-3 pb-3 border-b border-neutral-100 last:border-0 last:pb-0'
                                >
                                    <div className='w-2 h-2 rounded-full bg-brand-500 mt-1.5 flex-shrink-0' />
                                    <div className='flex-1 min-w-0'>
                                        <p className='text-sm text-neutral-900'>{actionLabel(a.action)}</p>
                                        <p className='text-xs text-neutral-500 mt-0.5'>{formatTimestamp(a.ts)}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <ConfirmModal
                open={showRemove}
                onCancel={() => setShowRemove(false)}
                onConfirm={handleRemove}
                title='Remove team member'
                description={`${member.name || member.email} will lose access to the portal immediately. Their past actions remain in the audit history. You can re-add them later.`}
                confirmText='Remove'
                cancelText='Cancel'
                intent='danger'
            />
        </div>
    )
}

function Stat({ label, value, info }: { label: string; value: string; info?: string }) {
    return (
        <div className='bg-white rounded-xl border border-neutral-200 p-4'>
            <div className='text-xs uppercase tracking-wider text-neutral-500 mb-2'>
                {label}
                {info && <InfoTip text={info} />}
            </div>
            <div className='text-xl font-bold text-neutral-900'>{value}</div>
        </div>
    )
}
